import React, { useMemo, useState } from "react";
import moment from "moment-timezone";
import { useNavigate } from "react-router-dom";
import { Copy } from "lucide-react";
import { CommonDestinationTimezone } from "@/lib/utils";
import { useListCfrTemplates } from "@/lib/api/cfr";
import { DataTable } from "@/components/ui/DataTable/index";
import { type DataTableColumn } from "@/components/ui/DataTable/index";
import { CommodityOptions } from "@/lib/api/commodity";
import { Button } from "@/components/ui/button";
import { DuplicateTemplateDialog } from "./components/DuplicateTemplateDialog";
import { RoutePath } from "@/section/router";

export interface CfrDocumentTemplateRow {
  id: string;
  created_at: string;

  commodity_type: string;
  crop_year: number;
  destination_id?: string | null;
  customer_destination_id?: string | null;


  version_label?: string;
  document_id: string;

  DocumentTemplate?: { title?: string; }

  MarketZone?: { id: string; dtn_market_zone: string; };
  Destination?: { id: string; long_name: string };
}

export function TemplateListSection() {
  const listResult = useListCfrTemplates();
  const navigate = useNavigate();
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [selectedTemplateForDuplicate, setSelectedTemplateForDuplicate] = useState<CfrDocumentTemplateRow | null>(null);

  const list_data = useMemo(() => {
    return listResult?.data?.data?.data || [];
  }, [listResult]);

  const columns = useMemo((): DataTableColumn<CfrDocumentTemplateRow>[] => [
    {
      id: "template_name",
      accessorKey: "template_name",
      header: "Template",
      cell: ({ row }) => {
        return row.original.DocumentTemplate?.title || "";
      },
    },
    {
      id: "commodity_type",
      accessorKey: "commodity_type",
      header: "Commodity Type",
      cell: (info) => {
        return CommodityOptions.find(item => item.value === info.getValue())?.label || info.getValue();
      },
    },
    {
      id: "crop_year",
      accessorKey: "crop_year",
      header: "Crop Year",
      cell: (info) => info.getValue() as string,
      aggregatedCell: () => "",
    },
    {
      id: "customer_destination_id",
      accessorKey: "customer_destination_id",
      header: "Destination",
      cell: ({ row }) => {
        return row.original.Destination?.long_name || "";
      },
    },
    {
      id: "created_at",
      accessorKey: "created_at",
      header: "Created",
        /* accessorFn: (row) => , */
      cell: (info) => moment.tz(info.getValue(), CommonDestinationTimezone).format('YYYY-MM-DD HH:mm'),
      enableFiltering: false,
    },
    {
      id: "actions",
      header: "Duplicate",
      cell: ({ row }) => {
        return (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedTemplateForDuplicate(row.original);
              setDuplicateDialogOpen(true);
            }}
          >
            <Copy className="h-4 w-4" />
          </Button>
        );
      },
      enableSorting: false,
      enableFiltering: false,
    },
  ], []);


  const isLoading = listResult.isLoading;
  const handleOnRowClick = (row: CfrDocumentTemplateRow) => {
    const path = RoutePath.CfrDocumentsTemplatesView.replace(":templateId", row.id);
    navigate(path);
  };

  return (
      <div className="flex flex-col gap-2">
        <DataTable
          stickyHeaders
          tableClassName="h-[calc(50vh-200px)] overflow-y-scroll"
          data={list_data}
          columns={columns}
          enableSorting
          enableMultiSort
          enableFiltering
          enableGrouping
          enableColumnVisibility
          filterStyle="select"
          filterMode="internal"
          defaultSorting={[{ id: "created_at", desc: true }]}
          emptyMessage={isLoading ? "Loading ..." : "No templates found"}
          rowClickable
          onRowClick={handleOnRowClick}
        />
        
        {selectedTemplateForDuplicate && (
          <DuplicateTemplateDialog
            open={duplicateDialogOpen}
            onOpenChange={(open) => {
              setDuplicateDialogOpen(open);
              if (!open) {
                setSelectedTemplateForDuplicate(null);
              }
            }}
            templateId={selectedTemplateForDuplicate.id}
            defaultCommodity={selectedTemplateForDuplicate.commodity_type}
            defaultCropYear={selectedTemplateForDuplicate.crop_year}
            defaultDestinationId={selectedTemplateForDuplicate.customer_destination_id || undefined}
            onSuccess={() => {
              listResult.refetch();
            }}
          />
        )}
      </div>
  );
}
